/**
 * Benachrichtigungen im Browser an- und abmelden.
 *
 * Der Ablauf ist bei allen Browsern derselbe: Service Worker anmelden, um
 * Erlaubnis fragen, beim Push-Dienst des Herstellers ein Abonnement lösen und
 * dessen Schlüssel an den eigenen Server geben. Verschieden ist nur, **wann**
 * es überhaupt geht:
 *
 * - **Android, Windows, Linux**: im Browser, ohne Weiteres.
 * - **iPhone und iPad**: erst, wenn die Seite über *Teilen → Zum
 *   Home-Bildschirm* installiert ist. Im Safari-Tab fehlt `PushManager`
 *   schlicht — deshalb wird dort gar nicht erst gefragt, sondern der Hinweis
 *   gezeigt.
 */

import { api } from '../api/client'
import { huelleAnmelden } from './huelle'
import { WORTLAUT } from '../texte'

export interface PushState {
  /** Kann dieser Browser überhaupt Benachrichtigungen empfangen? */
  supported: boolean
  /** Auf dem iPhone erst nach der Installation auf dem Home-Bildschirm. */
  needsInstall: boolean
  permission: NotificationPermission
  subscribed: boolean
  /**
   * Das Abonnement gehört zu einem anderen Schlüssel als dem, den der Server
   * jetzt hat. Dann weist der Push-Dienst jede Zustellung mit 403 ab, und die
   * Anmeldung muss erneuert werden.
   */
  stale: boolean
  /**
   * Auf diesem Gerät waren Benachrichtigungen schon einmal eingeschaltet.
   * Zusammen mit `subscribed: false` heißt das: die Anmeldung ist abhanden
   * gekommen, nicht "war nie da".
   */
  wanted: boolean
}

/**
 * Merkzettel: Auf diesem Gerät waren Benachrichtigungen einmal eingeschaltet.
 *
 * Nötig, weil ein Abonnement auch ohne Zutun verschwinden kann — iOS räumt
 * bei einer selten benutzten Web-App auf, und dann kommt einfach nichts mehr,
 * ohne dass irgendwo etwas danach aussieht. Ohne diesen Zettel kann die
 * Anwendung nicht unterscheiden zwischen "war nie an" und "ist abhanden
 * gekommen".
 *
 * Er liegt im `localStorage` und gehört damit zu den Website-Daten: wer die
 * löscht, löscht auch ihn — und dann ist ohnehin alles weg, Abonnement,
 * Erlaubnis und Service Worker.
 */
const MARKE = 'tb.push.gewuenscht'

export async function readState(): Promise<PushState> {
  const supported =
    'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window

  const subscription = supported ? await currentSubscription() : null
  const stale = subscription !== null && !(await matchesServerKey(subscription))

  return {
    supported,
    needsInstall: !supported && isIos(),
    permission: 'Notification' in window ? Notification.permission : 'default',
    // Ein Abonnement mit falschem Schlüssel ist keins: es sieht nur so aus,
    // und jede Zustellung liefe ins Leere.
    subscribed: subscription !== null && !stale,
    stale,
    wanted: localStorage.getItem(MARKE) !== null,
  }
}

/**
 * Gehört dieses Abonnement noch zum Schlüssel des Servers?
 *
 * Der Browser hebt den Schlüssel auf, mit dem er sich seinerzeit angemeldet
 * hat. Wird auf dem Server ein neues Paar eingetragen — schon ein zweiter
 * Aufruf des Schlüsselwerkzeugs erzeugt eines —, passt das alte Abonnement
 * nicht mehr, und der Push-Dienst antwortet mit 403. Das ist von außen nicht
 * zu sehen; hier ist es zu erkennen.
 */
async function matchesServerKey(subscription: PushSubscription): Promise<boolean> {
  try {
    const { key } = await api.get<{ key: string | null }>('/api/push/key')
    const eigener = subscription.options?.applicationServerKey
    if (!key || !eigener) return true

    return toBase64Url(new Uint8Array(eigener)) === key
  } catch {
    // Ohne Auskunft lieber nichts behaupten.
    return true
  }
}

function toBase64Url(bytes: Uint8Array): string {
  let roh = ''
  for (const byte of bytes) roh += String.fromCharCode(byte)
  return btoa(roh).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * Woran die Anmeldung gerade ist.
 *
 * **Der Grund ist ein Fall aus dem Betrieb** (#24, Windows): auf Knopfdruck
 * stand „Einen Moment …" und danach passierte nichts mehr. Genau einer dieser
 * Schritte kann unbegrenzt offen bleiben — `Notification.requestPermission()`
 * antwortet erst, wenn jemand die Frage beantwortet hat, und Chrome und Edge
 * stellen sie am Rechner nicht immer als Fenster, sondern als kleines Symbol
 * in der Adresszeile. Wer davon nichts weiß, wartet auf eine Antwort, die
 * niemand gibt.
 *
 * Ein Knopf, der stumm wartet, ist deshalb ein halber Fehler für sich. Die
 * Oberfläche sagt jetzt, worauf gewartet wird — und wo die Frage steckt.
 */
export type PushSchritt = 'schluessel' | 'erlaubnis' | 'worker' | 'abonnement' | 'server'

/**
 * Meldet dieses Gerät an.
 *
 * Muss aus einer Handlung heraus aufgerufen werden — ein Klick. Ungefragt
 * darf keine Seite nach der Erlaubnis fragen, und Browser lehnen es sonst
 * dauerhaft ab.
 */
export async function subscribe(melde: (schritt: PushSchritt) => void = () => {}): Promise<void> {
  melde('schluessel')
  const { key } = await api.get<{ enabled: boolean; key: string | null }>('/api/push/key')
  if (!key) throw new Error(WORTLAUT.technik.push.nichtEingerichtet)

  melde('erlaubnis')
  const erlaubnis = await Notification.requestPermission()
  if (erlaubnis !== 'granted') {
    throw new Error(
      erlaubnis === 'denied'
        ? WORTLAUT.technik.push.abgelehnt
        : WORTLAUT.technik.push.unbeantwortet,
    )
  }

  melde('worker')
  const registrierung = await huelleAnmelden()
  await navigator.serviceWorker.ready

  melde('abonnement')
  let abonnement = await registrierung.pushManager.getSubscription()

  // Ein Abonnement zum alten Schlüssel muss weg, bevor ein neues entstehen
  // kann -- der Browser gibt sonst dasselbe zurück und der Push-Dienst
  // antwortet weiter mit 403.
  if (abonnement && !(await matchesServerKey(abonnement))) {
    await abonnement.unsubscribe()
    abonnement = null
  }

  if (!abonnement) {
    try {
      abonnement = await registrierung.pushManager.subscribe({
        // Ohne das nimmt Chrome kein Abonnement an: der Inhalt muss
        // verschlüsselt sein, stille Nachrichten sind nicht erlaubt.
        userVisibleOnly: true,
        applicationServerKey: fromBase64Url(key),
      })
    } catch (fehler) {
      // Die Meldung des Browsers ist englisch und wenig hilfreich ("Registration
      // failed - permission denied" heißt oft schlicht: dieser Browser hat
      // keinen Push-Dienst erreicht).
      throw new Error(
        WORTLAUT.technik.push.keinAbonnement(
          fehler instanceof Error ? fehler.message : WORTLAUT.technik.push.unbekannterFehler,
        ),
      )
    }
  }

  melde('server')
  await meldeAnServer(abonnement)
  localStorage.setItem(MARKE, '1')
}

async function meldeAnServer(abonnement: PushSubscription): Promise<void> {
  const roh = abonnement.toJSON() as { endpoint?: string; keys?: Record<string, string> }
  await api.post('/api/push/subscriptions', {
    endpoint: roh.endpoint,
    p256dh: roh.keys?.p256dh,
    auth: roh.keys?.auth,
  })
}

/**
 * Prüft beim Start, ob dieses Gerät noch angemeldet ist — und meldet es
 * nötigenfalls von selbst wieder an.
 *
 * Der Anlass war ein Fall aus dem Betrieb: die Benachrichtigungen waren weg,
 * und erst Aus- und Einschalten brachte sie zurück. Genau das soll die
 * Anwendung selbst tun, denn niemand schaltet vorsorglich einen Schalter aus
 * und wieder ein, der scheinbar an ist.
 *
 * Zwei Fälle werden geheilt, beide ohne Rückfrage — die Erlaubnis liegt ja
 * schon vor, und ohne sie wird hier nichts unternommen:
 *
 * 1. Der Browser hat kein Abonnement mehr (iOS räumt auf). Dann wird ein
 *    neues gelöst.
 * 2. Der Browser hat eins, der Server aber nicht mehr — nach einem
 *    Rückspielen der Datenbank etwa. Das erneute Anmelden ist beim Server
 *    ein `ON DUPLICATE KEY UPDATE` und deshalb umsonst, wenn alles stimmt.
 *
 * Was hier bewusst **nicht** passiert: nach der Erlaubnis fragen. Das darf
 * nur auf Knopfdruck geschehen.
 */
export async function repariereAbo(): Promise<'nichts' | 'erneuert' | 'braucht-erlaubnis'> {
  if (localStorage.getItem(MARKE) === null) return 'nichts'
  if (!('serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window)) {
    return 'nichts'
  }
  if (Notification.permission !== 'granted') return 'braucht-erlaubnis'

  const { key } = await api.get<{ enabled: boolean; key: string | null }>('/api/push/key')
  if (!key) return 'nichts'

  const registrierung = await huelleAnmelden()
  await navigator.serviceWorker.ready

  let abonnement = await registrierung.pushManager.getSubscription()
  if (abonnement && !(await matchesServerKey(abonnement))) {
    await abonnement.unsubscribe()
    abonnement = null
  }

  if (abonnement === null) {
    abonnement = await registrierung.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: fromBase64Url(key),
    })
    await meldeAnServer(abonnement)
    return 'erneuert'
  }

  // Vorhanden -- dann nur dafür sorgen, dass der Server es auch weiß.
  await meldeAnServer(abonnement)
  return 'nichts'
}

/** Meldet dieses Gerät ab — beim Push-Dienst und auf dem eigenen Server. */
export async function unsubscribe(): Promise<void> {
  // Zuerst den Merkzettel weg: sonst hielte die Selbstheilung beim nächsten
  // Start das Abschalten für einen Verlust und meldete das Gerät wieder an.
  localStorage.removeItem(MARKE)

  const abonnement = await currentSubscription()
  if (abonnement) {
    await api.delete('/api/push/subscriptions')
    await abonnement.unsubscribe()
    return
  }
  // Kein Abonnement im Browser, aber vielleicht noch eine Zeile auf dem
  // Server -- etwa nach dem Löschen der Browserdaten.
  await api.delete('/api/push/subscriptions')
}

async function currentSubscription(): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator)) return null
  const registrierung = await navigator.serviceWorker.getRegistration()
  return (await registrierung?.pushManager.getSubscription()) ?? null
}

/** Der Schlüssel kommt als base64url, der Browser will Bytes. */
function fromBase64Url(text: string): Uint8Array<ArrayBuffer> {
  const gefuellt = (text + '='.repeat((4 - (text.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/')
  const roh = atob(gefuellt)

  const bytes = new Uint8Array(new ArrayBuffer(roh.length))
  for (let index = 0; index < roh.length; index++) {
    bytes[index] = roh.charCodeAt(index)
  }
  return bytes
}

function isIos(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    // iPad ab iPadOS 13 meldet sich als Mac; der Touchscreen verrät es.
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
}
